import { Alert, AlertDescription } from '@tempo-io/sui-alert';
import { Button } from '@tempo-io/sui-button';
import { Combobox } from '@tempo-io/sui-combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tempo-io/sui-dialog';
import { toast } from '@tempo-io/sui-sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SynchronizationStatusValues,
  WorkIntegration_Keys,
  type WorkIntegrationResponse,
} from '@/common/api/types/workIntegrations';
import { useItemTypesByIdQuery } from '@/common/queries/useItemTypesQuery';
import { useAssignItemIntegrationMutation } from '@/features/settings/work-integrations/mutations/useAssignItemIntegrationMutation';
import { useGetJiraItemTypeMappingsByInstanceIds } from '@/features/settings/work-integrations/queries/useGetJiraItemTypeMappingsByInstanceIds';

interface AssignIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: number;
  itemTypeId: number;
  integrations: WorkIntegrationResponse[];
  currentIntegrationId?: number;
}

interface IntegrationOption {
  value: string;
  label: string;
  instanceId: string;
  isMapped: boolean;
}

export const AssignIntegrationDialog = ({
  open,
  onOpenChange,
  itemId,
  itemTypeId,
  integrations,
  currentIntegrationId,
}: AssignIntegrationDialogProps) => {
  const { t } = useTranslation();

  const eligibleIntegrations = useMemo(
    () =>
      integrations
        .filter(
          (integration) =>
            integration.sourceSystemType === WorkIntegration_Keys.JIRA &&
            integration.synchronizationStatus === SynchronizationStatusValues.ENABLED,
        )
        .slice()
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [integrations],
  );

  const instanceIds = useMemo(
    () => eligibleIntegrations.map((integration) => integration.instanceId),
    [eligibleIntegrations],
  );
  const { mappedTypeIdsByInstanceId } = useGetJiraItemTypeMappingsByInstanceIds(instanceIds);
  const { data: itemTypesById = {} } = useItemTypesByIdQuery();
  const itemTypeName = itemTypesById[itemTypeId]?.name ?? String(itemTypeId);

  const options: IntegrationOption[] = useMemo(
    () =>
      eligibleIntegrations.map((integration) => ({
        value: String(integration.id),
        label:
          integration.name ??
          t(($) => $.adaptivePlanner.itemPanel.integration.unnamedFallback, { id: integration.id }),
        instanceId: integration.instanceId,
        isMapped: mappedTypeIdsByInstanceId.get(integration.instanceId)?.has(itemTypeId) ?? false,
      })),
    [eligibleIntegrations, t, mappedTypeIdsByInstanceId, itemTypeId],
  );

  const initialSelection = useMemo<IntegrationOption | null>(() => {
    if (currentIntegrationId === undefined) return null;
    return options.find((opt) => opt.value === String(currentIntegrationId)) ?? null;
  }, [currentIntegrationId, options]);

  const [selection, setSelection] = useState<IntegrationOption | null>(initialSelection);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSelection(initialSelection);
    onOpenChange(nextOpen);
  };

  const { mutate, isPending } = useAssignItemIntegrationMutation();

  const handleConfirm = () => {
    if (!selection) return;
    const integrationId = Number(selection.value);
    mutate(
      { itemId, integrationId, currentIntegrationId },
      {
        onSuccess: () => {
          toast.success(
            t(($) => $.adaptivePlanner.itemPanel.integration.dialog.confirmSuccessToast),
          );
          handleOpenChange(false);
        },
        onError: () =>
          toast.error(t(($) => $.adaptivePlanner.itemPanel.integration.dialog.confirmErrorToast)),
      },
    );
  };

  const title =
    currentIntegrationId === undefined
      ? t(($) => $.adaptivePlanner.itemPanel.integration.dialog.titleAdd)
      : t(($) => $.adaptivePlanner.itemPanel.integration.dialog.titleChange);

  const confirmDisabled =
    !selection || selection.value === String(currentIntegrationId) || isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Alert variant="warning">
          <AlertDescription>
            {t(($) => $.adaptivePlanner.itemPanel.integration.dialog.warning)}
          </AlertDescription>
        </Alert>

        <div className="mt-2">
          <Combobox<IntegrationOption>
            options={options}
            value={selection}
            onValueChange={(option) => setSelection((option as IntegrationOption | null) ?? null)}
            label={t(($) => $.adaptivePlanner.itemPanel.integration.dialog.dropdownPlaceholder)}
            emptyMessage={t(($) => $.adaptivePlanner.itemPanel.integration.dialog.dropdownEmpty)}
            className="w-full"
            modal
            isOptionDisabled={(option) => !option.isMapped}
            renderOption={({ option, isDisabled }) =>
              isDisabled ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block w-full text-text-disabled">{option.label}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {t(
                        ($) =>
                          $.adaptivePlanner.itemPanel.integration.dialog
                            .dropdownItemUnmappedTooltip,
                        { typeName: itemTypeName },
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span>{option.label}</span>
              )
            }
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            {t(($) => $.adaptivePlanner.itemPanel.integration.dialog.cancel)}
          </Button>
          <Button onClick={handleConfirm} disabled={confirmDisabled}>
            {t(($) => $.adaptivePlanner.itemPanel.integration.dialog.confirm)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
